import * as THREE from 'three';
import { COMMON_UNIFORMS, LIGHT_FNS, NOISE_FNS, SKY_FNS } from '../shaders/common';
import type { SharedUniforms } from '../world/Environment';

export type PatchOptions = {
  /** Strength of the backlit rim glow (fabric fuzz / straw edges catching the sun). */
  rim?: number;
  /** Vertex flutter driven by wind, weighted by the `flutter` attribute. */
  flutter?: boolean;
  /** Banner wave: local +X runs away from the pole, displacement along local Z. */
  wave?: number;
};

/** Adds the landscape's aerial perspective, cloud shadows and optional backlit rim to a stock PBR material. */
export function patchMaterial<T extends THREE.MeshStandardMaterial>(material: T, uniforms: SharedUniforms, options: PatchOptions = {}): T {
  const rim = options.rim ?? 0;
  const key = `kaze-${options.flutter ? 'flutter' : 'still'}-${options.wave ? 'wave' : 'flat'}`;
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.uniforms.uRimStrength = { value: rim };
    shader.uniforms.uWaveAmp = { value: options.wave ?? 0 };
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
        varying vec3 vKazeWorldPos;
        ${options.flutter ? 'attribute float flutter;\n' : ''}
        ${options.flutter || options.wave ? 'uniform float uTime;\nuniform vec2 uWindDir;\nuniform float uWindStrength;\nuniform float uWaveAmp;' : ''}`,
      )
      .replace(
        '#include <project_vertex>',
        `${
          options.flutter
            ? `{
              float ph = dot(transformed.xz, vec2(3.1, 2.3)) + uTime * 7.0;
              vec3 f = vec3(sin(ph) * 0.6 + sin(ph * 1.7 + 1.3) * 0.4, sin(ph * 1.3 + 0.7) * 0.3, cos(ph * 0.9) * 0.6);
              transformed += f * flutter * 0.018 * (0.4 + uWindStrength);
            }`
            : ''
        }
        ${
          options.wave
            ? `{
              float along = max(transformed.x, 0.0);
              float ph = along * 6.5 - uTime * (4.0 + uWindStrength * 4.0) + transformed.y * 1.3;
              transformed.z += (sin(ph) * 0.8 + sin(ph * 1.9 + 0.7) * 0.25) * uWaveAmp * along * (0.45 + uWindStrength);
              transformed.x -= abs(sin(ph)) * uWaveAmp * along * 0.3;
            }`
            : ''
        }
        #include <project_vertex>
        {
          vec4 kazeWp = vec4(transformed, 1.0);
          #ifdef USE_INSTANCING
          kazeWp = instanceMatrix * kazeWp;
          #endif
          vKazeWorldPos = (modelMatrix * kazeWp).xyz;
        }`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
        ${COMMON_UNIFORMS}
        uniform float uRimStrength;
        varying vec3 vKazeWorldPos;
        ${NOISE_FNS}
        ${SKY_FNS}
        ${LIGHT_FNS}`,
      )
      .replace(
        '#include <lights_fragment_end>',
        `#include <lights_fragment_end>
        {
          float kazeCloud = cloudShadowAt(vKazeWorldPos);
          reflectedLight.directDiffuse *= kazeCloud;
          reflectedLight.directSpecular *= kazeCloud;
          vec3 kazeV = normalize(cameraPosition - vKazeWorldPos);
          vec3 kazeN = normalize(normal);
          float kazeFres = pow(1.0 - clamp(abs(dot(kazeN, geometryViewDir)), 0.0, 1.0), 3.0);
          float kazeBack = pow(clamp(dot(-kazeV, uSunDir), 0.0, 1.0), 2.0);
          reflectedLight.directDiffuse += uSunColor * kazeFres * (0.25 + kazeBack) * uRimStrength * kazeCloud * diffuseColor.rgb * 2.0;
        }`,
      )
      .replace('#include <fog_fragment>', 'gl_FragColor.rgb = applyAtmosphere(gl_FragColor.rgb, vKazeWorldPos);');
  };
  material.customProgramCacheKey = () => key;
  return material;
}
